import React, { useEffect, useId, useRef, useState } from 'react';
import { Edit3, Flag, MoreHorizontal, Power, Trash2 } from 'lucide-react';
import { t } from '../i18n';

function MarketOwnerActions({ status, busy = false, onEdit, onTogglePublication, onDelete, onReport }) {
  const [menuOpen, setMenuOpen] = useState(false);
  const menuId = useId();
  const triggerId = useId();
  const containerRef = useRef(null);
  const triggerRef = useRef(null);
  const menuItemRefs = useRef([]);
  const published = status === 'active';

  useEffect(() => {
    if (!menuOpen) return undefined;
    menuItemRefs.current.find((item) => item && !item.disabled)?.focus();
    const dismissOutside = (event) => {
      if (!containerRef.current?.contains(event.target)) setMenuOpen(false);
    };
    document.addEventListener('pointerdown', dismissOutside);
    return () => document.removeEventListener('pointerdown', dismissOutside);
  }, [menuOpen]);

  const closeMenu = () => {
    setMenuOpen(false);
    triggerRef.current?.focus();
  };

  const handleMenuKeyDown = (event) => {
    if (event.key === 'Escape' && menuOpen) {
      event.preventDefault();
      event.stopPropagation();
      closeMenu();
      return;
    }
    if (!['ArrowDown', 'ArrowUp', 'Home', 'End'].includes(event.key)) return;
    event.preventDefault();
    if (!menuOpen) {
      setMenuOpen(true);
      return;
    }
    const items = menuItemRefs.current.filter((item) => item && !item.disabled);
    if (!items.length) return;
    const currentIndex = items.indexOf(document.activeElement);
    let nextIndex = 0;
    if (event.key === 'End') nextIndex = items.length - 1;
    else if (event.key === 'ArrowUp') nextIndex = currentIndex <= 0 ? items.length - 1 : currentIndex - 1;
    else if (event.key === 'ArrowDown') nextIndex = currentIndex < 0 || currentIndex === items.length - 1 ? 0 : currentIndex + 1;
    items[nextIndex]?.focus();
  };

  return (
    <div className="market-owner-actions" role="group" aria-label={t('market.ownerActions')}>
      {onEdit && <button type="button" className="market-owner-action" aria-label={t('market.editAction')} title={t('market.editAction')} disabled={busy} onClick={onEdit}>
        <Edit3 size={18} aria-hidden="true" />
        <span>{t('market.editAction')}</span>
      </button>}
      <div
        ref={containerRef}
        className="market-owner-more"
        onBlur={(event) => {
          if (!event.currentTarget.contains(event.relatedTarget)) setMenuOpen(false);
        }}
        onKeyDown={handleMenuKeyDown}
      >
        <button
          ref={triggerRef}
          id={triggerId}
          type="button"
          className="market-owner-action market-owner-action-more"
          disabled={busy}
          aria-label={t('market.moreActions')}
          title={t('market.moreActions')}
          aria-expanded={menuOpen}
          aria-haspopup="menu"
          aria-controls={menuOpen ? menuId : undefined}
          onClick={() => setMenuOpen((open) => !open)}
        >
          <MoreHorizontal size={21} aria-hidden="true" />
        </button>
        {menuOpen && (
          <div id={menuId} role="menu" aria-labelledby={triggerId} className="market-owner-menu">
            {onTogglePublication && <button
              ref={(element) => { menuItemRefs.current[0] = element; }}
              type="button"
              role="menuitemcheckbox"
              aria-checked={published}
              disabled={busy}
              className={published ? 'is-published' : 'is-unpublished'}
              onClick={() => { closeMenu(); onTogglePublication(); }}
            >
              <Power size={16} strokeWidth={2.4} aria-hidden="true" />
              <span>{t(published ? 'market.publicationOn' : 'market.publicationOff')}</span>
            </button>}
            {onDelete && <button
              ref={(element) => { menuItemRefs.current[1] = element; }}
              type="button"
              role="menuitem"
              disabled={busy}
              className="is-danger"
              onClick={() => { closeMenu(); onDelete(); }}
            >
              <Trash2 size={16} aria-hidden="true" />
              <span>{t('market.delete')}</span>
            </button>}
            {onReport && <button
              ref={(element) => { menuItemRefs.current[0] = element; }}
              type="button"
              role="menuitem"
              disabled={busy}
              onClick={() => { closeMenu(); onReport(); }}
            >
              <Flag size={16} aria-hidden="true" />
              <span>{t('market.report')}</span>
            </button>}
          </div>
        )}
      </div>
    </div>
  );
}

export default MarketOwnerActions;
